import { useRef, useCallback } from 'react'
import { copyToClipboard } from 'shared/utils'
import useFormattedValue from '../../hooks/useFormattedValue'
import useTableContext from '../../hooks/useTableContext'
import { TableCellProps } from './TableCell.types'
import EditableCellInput from './EditableCellInput'
import { useCursorState } from './hooks/useCursorState'
import { StyledTableCell, CellContent, EditableCell } from './TableCell.styles'
import { CURSOR_STATES } from './TableCell.constants'
import { useTranslation } from 'react-i18n-lite'

export default function TableCell({
  columnName,
  value,
  objectUuidOrIndex,
  dateOptions
}: TableCellProps) {
  const { columnsLayouts, fields, mode, selectedRows } = useTableContext()
  const cellRef = useRef<HTMLTableCellElement>(null)

  const {
    cursorState,
    getCursorStyle,
    handleMouseEnter,
    handleMouseLeave,
    setCopied
  } = useCursorState({ ref: cellRef })

  const { t } = useTranslation()

  const { type } = fields?.[columnName] || {}
  const formattedValue = useFormattedValue({ value, type, dateOptions })
  const columnLayout = columnsLayouts.find(
    (layout) => layout.name === columnName
  )
  const textWrapping = columnLayout?.text_wrapping

  const copyValueToClipboard = useCallback(() => {
    if (formattedValue) {
      copyToClipboard(formattedValue)
      setCopied()
    }
  }, [formattedValue, setCopied])

  const selectedRowsArray = Object.keys(selectedRows ?? {})
  const isSelected = selectedRowsArray.includes(objectUuidOrIndex)
  const isEditable =
    mode === 'insert' ||
    (mode === 'edit' && (isSelected || !selectedRowsArray.length))

  if (isEditable) {
    return (
      <EditableCell ref={cellRef}>
        <EditableCellInput
          value={value}
          columnName={columnName}
          objectUuidOrIndex={objectUuidOrIndex}
          formattedValue={formattedValue}
        />
      </EditableCell>
    )
  }

  return (
    <StyledTableCell
      ref={cellRef}
      hasTextWrapping={Boolean(textWrapping)}
      onDoubleClick={copyValueToClipboard}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: getCursorStyle() }}
      title={t('data-tables.double-click')}
      role='cell'
      aria-label={`${columnName}: ${formattedValue}`}
      data-testid={`table-cell-${columnName}`}
    >
      <CellContent
        isCopied={cursorState === CURSOR_STATES.COPIED}
        role='presentation'
      >
        {formattedValue}
      </CellContent>
    </StyledTableCell>
  )
}
